import { type ChangeEvent, useCallback, useRef } from 'react'

import {
  CircleLoader,
  EmptyState,
  ErrorAlert,
  Header,
  IconSearch,
  Pagination,
  Space,
  TextButton,
  TextField,
  WithOverlayLoader,
} from '@procurify/ui'
import { useTheme } from 'styled-components'

import { useProcurifyIntl } from '@webapp/hooks'
import { SecondaryCategoryRow } from './components/SecondaryCategoryRow'
import messages from './messages'
import { SearchIconFrameStyled } from './styles/SearchIconFrameStyled'
import { SelectAllBarStyled } from './styles/SelectAllBarStyled'
import { type SecondaryCategoryRowViewModel } from '../../types'

export interface IReportAccessSecondaryCategoryListViewProps {
  primaryCategoryName: string
  rows: SecondaryCategoryRowViewModel[]
  isAllSecondaryCategoriesSelected: boolean
  isSelectAllBarVisible: boolean
  showInitialLoader: boolean
  showErrorState: boolean
  showEmptyState: boolean
  showEmptyStateContent: boolean
  showList: boolean
  isRefetching: boolean
  searchTerm: string
  onSearchChange: (term: string) => void
  currentPage: number
  numPages: number
  onPageChange: (page: number) => void
  onRowClick: (secondaryCategoryId: number) => void
  onToggleSelectAll: () => void
  onRetry: () => void
}

export const ReportAccessSecondaryCategoryListView = ({
  primaryCategoryName,
  rows,
  isAllSecondaryCategoriesSelected,
  isSelectAllBarVisible,
  showInitialLoader,
  showErrorState,
  showEmptyState,
  showEmptyStateContent,
  showList,
  isRefetching,
  searchTerm,
  onSearchChange,
  currentPage,
  numPages,
  onPageChange,
  onRowClick,
  onToggleSelectAll,
  onRetry,
}: IReportAccessSecondaryCategoryListViewProps) => {
  const intl = useProcurifyIntl()
  const theme = useTheme()

  const searchInputRef = useRef<HTMLInputElement>(null)

  // "Try again" unmounts with the error; the search field is in every state
  const handleRetry = useCallback(() => {
    searchInputRef.current?.focus()
    onRetry()
  }, [onRetry])

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      onSearchChange(event.target.value),
    [onSearchChange]
  )

  const renderList = () => {
    if (showList) {
      return (
        <WithOverlayLoader
          visible={isRefetching}
          text={intl.formatMessage(messages.loadingText)}
        >
          {isSelectAllBarVisible && (
            <SelectAllBarStyled
              type='button'
              aria-pressed={isAllSecondaryCategoriesSelected}
              onClick={onToggleSelectAll}
            >
              {isAllSecondaryCategoriesSelected
                ? intl.formatMessage(messages.allSelectedLabel)
                : intl.formatMessage(messages.selectAllLabel)}
            </SelectAllBarStyled>
          )}

          {rows.map((row) => (
            <SecondaryCategoryRow
              key={row.id}
              secondaryCategoryId={row.id}
              name={row.name}
              isActive={row.isActive}
              onSelect={onRowClick}
            />
          ))}
        </WithOverlayLoader>
      )
    }

    if (showInitialLoader) {
      return (
        <Space direction='horizontal' justify='center'>
          <CircleLoader color={theme.colors.text.secondary} />
        </Space>
      )
    }

    if (showErrorState) {
      return (
        <ErrorAlert
          action={
            <TextButton type='button' onClick={handleRetry}>
              {intl.formatMessage(messages.retryButtonLabel)}
            </TextButton>
          }
        >
          {intl.formatMessage(messages.errorStateMessage)}
        </ErrorAlert>
      )
    }

    if (showEmptyState) {
      return (
        <EmptyState
          small
          header={intl.formatMessage(messages.emptyStateHeader)}
          content={
            showEmptyStateContent
              ? intl.formatMessage(messages.emptyStateContent)
              : undefined
          }
        />
      )
    }

    return null
  }

  return (
    <Space>
      <Header as='h4' variant='h5'>
        {intl.formatMessage(messages.heading, { primaryCategoryName })}
      </Header>

      <TextField
        fullWidth
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        value={searchTerm}
        onChange={handleSearchChange}
        inputRef={searchInputRef}
        InputProps={{
          endAdornment: (
            <SearchIconFrameStyled>
              <IconSearch size={16} aria-hidden='true' />
            </SearchIconFrameStyled>
          ),
        }}
        inputProps={{
          'aria-label': intl.formatMessage(messages.searchAriaLabel),
        }}
      />

      {renderList()}

      {showList && (
        <Space direction='horizontal' justify='center'>
          <Pagination
            currentPage={currentPage}
            numPages={numPages}
            onPageChange={onPageChange}
          />
        </Space>
      )}
    </Space>
  )
}

ReportAccessSecondaryCategoryListView.displayName =
  'ReportAccessSecondaryCategoryListView'
